import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Users, Loader2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const OwnershipSyncCard = React.memo(function OwnershipSyncCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/admin/ownership-sync/preview`);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load preview");
    }
    setLoading(false);
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/ownership-sync/run`);
      setResult(res.data);
      setPreview(null);
      toast.success(`Fixed ${res.data.companies_fixed} companies and ${res.data.leads_fixed} leads`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
    setRunning(false);
  };

  const hasIssues = preview && (preview.companies_to_fix > 0 || preview.mismatched_leads > 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          Ownership Sync
        </CardTitle>
        <CardDescription>
          Ensure every company's owner matches its leads' owner. All leads under a company must share the same owner.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Preview results */}
        {preview && (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-slate-800">{preview.total_companies}</p>
                <p className="text-[10px] text-slate-500 uppercase font-medium">Companies</p>
              </div>
              <div className={`rounded-lg p-2.5 text-center ${preview.companies_to_fix > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                <p className={`text-lg font-bold ${preview.companies_to_fix > 0 ? 'text-amber-700' : 'text-green-700'}`}>{preview.companies_to_fix}</p>
                <p className={`text-[10px] uppercase font-medium ${preview.companies_to_fix > 0 ? 'text-amber-600' : 'text-green-600'}`}>Wrong Owner</p>
              </div>
              <div className={`rounded-lg p-2.5 text-center ${preview.mismatched_leads > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <p className={`text-lg font-bold ${preview.mismatched_leads > 0 ? 'text-red-700' : 'text-green-700'}`}>{preview.mismatched_leads}</p>
                <p className={`text-[10px] uppercase font-medium ${preview.mismatched_leads > 0 ? 'text-red-600' : 'text-green-600'}`}>Mismatched Leads</p>
              </div>
            </div>
            {!hasIssues && (
              <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                <CheckCircle2 className="h-3.5 w-3.5" />
                All ownership is in sync. Nothing to fix.
              </div>
            )}
            {hasIssues && (
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5" />
                {preview.companies_to_fix} companies and {preview.mismatched_leads} leads need fixing.
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Done: {result.companies_fixed} companies and {result.leads_fixed} leads fixed.
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={loading || running}
            className="flex-1"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Scan for Issues
          </Button>
          {hasIssues && (
            <Button
              onClick={handleRun}
              disabled={running}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Users className="h-4 w-4 mr-1.5" />}
              Fix Ownership
              <Badge variant="secondary" className="ml-2 bg-blue-500 text-white text-[10px] px-1.5">
                {preview.companies_to_fix + preview.mismatched_leads}
              </Badge>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default OwnershipSyncCard;
